<template>
  <s-modal
    v-model="showModal"
    class="list-modal text-left"
    footer-class="list-modal__footer"
    :size="modalSize"
    :cancel-confirm-message-code="cancelMessageCode"
    :ok-confirm-message-code="okMessageCode"
    :confirm-type="confirmationTypes.YES_NO"
    :ok-only="!configurationMode"
    :ok-title="okTitle"
    :title="title"
    @cancel="cancel"
    @ok="confirmSelection"
    @show="show"
    @hidden="onHide"
  >
    <b-container
      v-if="!!totalItems"
      class="p-0"
      fluid
    >
      <b-row
        no-gutters
        align-h="end"
      >
        <b-col
          v-if="configurationMode"
          class="mb-3"
          cols="auto"
        >
          <b-button
            class="suppressed-items__remove-all"
            :variant="buttonVariantType"
            @click="removeAll"
          >
            <i
              class="fas fa-times-circle mr-2"
              aria-hidden="true"
            />
            {{ $t('actions.remove-all') }}
          </b-button>
        </b-col>
      </b-row>
      <s-table
        v-if="multipleColumns"
        class="no-align"
        :items="modalTableItems"
        :fields="modalTableHeaders"
      >
      </s-table>
      <s-table
        v-else
        class="suppressed-items__table no-align"
        sort-by="name"
        local-sorting
        fixed
        :sort-direction="sort"
        :items="isPaginated ? paginatedItems : items"
        :fields="fieldsHandler"
      >
        <template #head(actions)>
          <div class="suppressed-items__check-wrapper"></div>
        </template>
        <template #cell(actions)="data">
          <i
            class="fas fa-times-circle suppressed-items__remove-icon"
            aria-hidden="true"
            @click="removeItem(data)"
          />
        </template>
        <template #cell()="data">
          <div
            v-if="isHtml"
            v-html="data.value"
          ></div>
          <div v-else>
            {{ data.value }}
          </div>
        </template>
      </s-table>

      <grid-paginator
        v-if="isPaginated"
        v-model="currentPage"
        class="modal-paginator"
        :total-rows="totalItems"
        :per-page="perPage"
        @input="paging()"
      />

    </b-container>
    <b-container
      v-if="!totalItems"
      fluid
    >
      <s-empty-list/>
    </b-container>
  </s-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import ConfirmationTypes from '@/constants/confirmationTypes';
import GridPaginator from '@/components/lists/Paginator';

export default {
  name: 'SuppressedItemsModal',
  components: {
    GridPaginator,
  },
  props: {
    value: {
      type: Boolean,
    },
    configurationMode: {
      type: Boolean,
      default: false,
    },
    modalItemNameKeys: {
      type: Array,
    },
    modalItemIdKey: {
      type: String,
    },
    modalTableItems: {
      type: Array,
    },
    modalTitle: {
      type: String,
    },
    modalTableHeaders: {
      type: Array,
    },
    sort: {
      type: String,
      default: 'asc',
    },
    isPaginated: {
      type: Boolean,
      default: false,
    },
    perPage: {
      type: Number,
      default: 15,
    },
    customModalSize: {
      type: String || null,
      default: null,
    },
    isHtml: {
      type: Boolean,
      default: false,
    },
    multipleColumns: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    items: [],
    title: null,
    tableHeaders: null,
    paginatedItems: [],
    currentPage: 1,
  }),
  computed: {
    ...mapGetters([
      'user',
      'currentFontSize',
    ]),
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    modalSize() {
      if (this.customModalSize) return this.customModalSize;
      return this.currentFontSize.includes('18') ? 'lg' : 'md';
    },
    cancelMessageCode() {
      return this.configurationMode ? 'MC006' : null;
    },
    okMessageCode() {
      return this.configurationMode ? 'MC011' : null;
    },
    okTitle() {
      return this.configurationMode ? this.$t('actions.confirm') : this.$t('general.ok');
    },
    buttonVariantType() {
      return this.user.nightMode ? 'outline-light' : 'outline-dark';
    },
    fieldsHandler() {
      return this.configurationMode ? this.configurationFields : this.fields;
    },
    fields() {
      return (this.tableHeaders || []).map((item, index) => ({
        key: this.modalItemNameKeys?.[index],
        label: item,
        sortable: false,
        class: 'text-left',
      }));
    },
    configurationFields() {
      return [
        {
          key: 'actions',
          class: 'suppressed-items__cell-check',
          sortable: false,
        },
        ...this.fields,
      ];
    },
    confirmationTypes() {
      return ConfirmationTypes;
    },
    paginateFirstIndex() {
      return (this.currentPage - 1) * this.perPage;
    },
    paginateLastIndex() {
      return (this.currentPage * this.perPage);
    },
    totalItems() {
      return (this.items || []).length;
    },
  },
  methods: {
    cancel() {
      this.showModal = false;
    },
    removeAll() {
      this.$confirmMessage({
        code: 'MC029',
      }).accepted(() => {
        this.items = [];
      });
    },
    removeItem({ item }) {
      this.items = this.items
        .filter(oldItens => oldItens[this.modalItemIdKey] !== item[this.modalItemIdKey]);

      if (this.isPaginated) {
        this.paging();
      }
    },
    handleGridItems(items, title, tableHeaders) {
      this.tableHeaders = tableHeaders;
      this.title = title;
      this.items = items;
    },
    show() {
      this.handleGridItems(this.modalTableItems, this.modalTitle, this.modalTableHeaders);

      if (this.isPaginated) {
        this.currentPage = 1;
        this.paging();
      }
    },
    onHide() {
      this.handleGridItems([], null);
    },
    confirmSelection() {
      this.$emit('update-selection', this.items);
      this.showModal = false;
    },
    getPaginatedItems() {
      return (this.items || [])
        .slice(this.paginateFirstIndex, this.paginateLastIndex);
    },
    paging() {
      this.paginatedItems = this.getPaginatedItems();
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.modal-body
  padding-top: 0 !important

  & .modal-paginator
    margin-top: 10px

    ::v-deep .paginator
      &__summary-label
        padding-left: 0

.suppressed-items
  &__remove-all
    color: var(--font-color-primary)
    min-width: 30px !important
    min-height: 20px !important
    font-size: .9em

    &:hover
      color: var(--color-page-background)

  &__table-container
    display: flex
    flex-direction: column

  &__table ::v-deep
    height: 55vh
    margin-bottom: 0

    table
      display: flex
      flex-direction: column
      +media-min-sm()
        min-width: 450px

    tbody
      display: block
      overflow-y: auto
      overflow-x: hidden
      height: 100%

    thead
      position: sticky
      top: 0
      z-index: 1000

      th:not(:first-child):not(:nth-child(2))
        text-align: center !important

    thead, th
      background: var(--color-background-dark)

    tbody, thead
      tr
        display: table
        width: 100%
        table-layout: fixed

      th:first-child,
      td:first-child
        width: 35px

      th:nth-child(2),
      td:nth-child(2)
        border-right: 2px solid var(--color-table-body-bg)

  &__loader
    font-size: 2em
    margin-bottom: 1em

  &__remove-icon
    cursor: pointer
    transition: all .3s

    &:hover
      color: var(--color-danger)

  &__check-wrapper
    width: 35px
    position: relative

    & .custom-control
      position: absolute
      bottom: 0
</style>
